import { computed, ref, watch } from "vue";
import { productKind, recodeKindFromTypeKey } from "@utils/recodeKinds";
import { RECODE_ITEMS } from "@/composables/useRecodeTypeItems";
import { defineStore, storeToRefs } from "pinia";
import cardFieldsDescriptor from "./cardFieldsDescriptor";
import { updateCardPayload } from "../cardPayloadHandler";
import { useConstantsStore } from "../../constants";
import { useCardsStore } from "../cards";
import { useSurveyCardsStore } from "../surveyCards";
import { cardTypeName } from "@utils/constants/cardTypes";

import { set, isEmpty, camelizeObject } from "@utils/toolBox";

export const useCurrentCardStore = defineStore("currentCard", () => {
  const { cardSubTypesInfos, cardSettings } = storeToRefs(useConstantsStore());
  const cardsStore = useCardsStore();
  const surveyCardsStore = useSurveyCardsStore();
  // state
  const settings = ref({});
  const currentCard = ref({});
  const currentIndex = ref(-1);
  const currentToken = ref("");
  const fields = ref([]);
  const cardPane = ref("content");

  watch(
    () => currentCard.value?.specs,
    (specs, oldSpecs) => {
      if (specs != null && specs === oldSpecs && currentToken.value) cardsStore.updateCard(currentToken.value);
    },
    { deep: true },
  );

  // getters
  const currentType = computed(() => currentCard.value?.groupType ?? null);
  const currentSubType = computed(() => {
    const card = currentCard.value;
    if (card?.type === "q_hidden") return RECODE_ITEMS.find((item) => item.key === productKind(card))?.name ?? "Recode";
    return cardTypeName(card?.type, cardSubTypesInfos.value[card?.type]?.name);
  });
  const currentConditions = computed(() => currentCard.value?.specs?.conditions ?? []);
  const currentIsHidden = computed(() => currentCard.value?.specs?.hidden ?? false);
  const currentSubTypeOptions = computed(() => cardSubTypesInfos.value[currentCard.value?.type]?.options);
  const preceedingCards = computed(() => surveyCardsStore.cards.slice(0, currentIndex.value));
  const computedCardFields = computed(() => fields.value?.filter((field) => checkFilters(field?.displayFilters)));
  const currentSpecs = computed(() => currentCard.value?.specs ?? {});
  const isSettingHidden = computed(() => ({
    compactLayout: currentCard.value?.type === "q_matrix" && ["nps", "rating"].includes(currentSpecs.value.matrix_type),
    translatable:
      !["q_open", "q_open_multiple"].includes(currentCard.value?.type) && currentSpecs.value.is_other !== true,
    randomizeAnswers:
      currentCard.value?.type === "q_matrix" && ["nps", "rating"].includes(currentSpecs.value.matrix_type),
    dynamicAnswers:
      currentCard.value?.type === "q_matrix" && ["nps", "rating"].includes(currentSpecs.value.matrix_type),
    answerConditions:
      currentCard.value?.type === "q_matrix" && ["nps", "rating"].includes(currentSpecs.value.matrix_type),
    limits: currentCard.value?.type === "q_matrix" && currentSpecs.value.matrix_type !== "multiple",
    exclusiveAnswers: currentCard.value?.type === "q_matrix" && currentSpecs.value.matrix_type !== "multiple",
    screenout:
      currentCard.value?.type === "q_hidden" &&
      currentSpecs.value.transformation_type === "Mailing contact attribute (Open)",
  }));
  const isInLoop = computed(() => currentSpecs.value.loop_token != null);

  // actions
  const checkFilters = (filters = []) => {
    if (isEmpty(filters)) return true;

    const currentCardType = currentCard.value.type;
    const relevantFilters = filters.filter(({ cardType = null }) => cardType === currentCardType || cardType === null);

    // If no relevant filters, display the card (condition does not count)
    if (isEmpty(relevantFilters)) return true;

    // Check if any relevant filter condition matches (OR condition between relevant filters)
    return relevantFilters.some(({ cardType = null, field, value, operator }) => {
      const fieldValue = currentCard.value?.specs?.[field];

      // Handle filters without cardType
      if (cardType === null) return operator === "not" ? fieldValue !== value : fieldValue === value;

      // Determine if the field value matches the condition based on the operator
      if (operator === "not") return Array.isArray(value) ? !value.includes(fieldValue) : fieldValue !== value;

      return Array.isArray(value) ? value.includes(fieldValue) : fieldValue === value;
    });
  };

  const defineCardFields = ({ type }) =>
    (fields.value = cardFieldsDescriptor[type].filter((field) => field?.edit !== false));
  const defineCardSettings = () => {
    const sortedSettings = {};
    currentSubTypeOptions.value.forEach((optionName) => (sortedSettings[optionName] = cardSettings.value[optionName]));
    settings.value = camelizeObject(sortedSettings);
  };

  const handleListFields = () => {
    const specs = currentCard.value.specs ?? (currentCard.value.specs = {});
    fields.value.forEach(({ isList = false, name, alias }) => {
      const fieldName = alias ?? name;
      if (isList && specs[fieldName] == null) set(specs, fieldName, []);
    });
  };

  const selectCard = ({ card, index, selectConditions }) => {
    if (card == null) return;
    if (card.token.includes("_gen")) {
      //don't display country or profile card, reset currentCard
      currentCard.value = {};
      currentIndex.value = -1;
      currentToken.value = "";
      cardPane.value = "content";
      fields.value = [];
      settings.value = {};
    } else {
      currentCard.value = card;
      currentIndex.value = index;
      currentToken.value = card.token;
      cardPane.value = selectConditions ? "conditions" : "content";
      defineCardFields({ type: card.type });
      defineCardSettings();
      handleListFields();
    }
  };

  const updateCardType = ({ value }) => {
    const card = updateCardPayload({ card: currentCard.value, type: surveyCardsStore.cardTypeFor(value) });
    currentCard.value = surveyCardsStore.recodePreset(card, recodeKindFromTypeKey(value));
    surveyCardsStore.updateCurrentCard({ index: currentIndex.value, card: currentCard.value });
    defineCardFields({ type: card.type });
    defineCardSettings();
    handleListFields();
  };

  const updateCardName = ({ value }) => {
    currentCard.value.name = value;
    if (currentToken.value) cardsStore.updateCard(currentToken.value);
  };

  const isCheckedOptions = () => {
    const checkboxs = ["is_other", "screenout", "exclusive_answers", "has_dynamic_answers"];
    return checkboxs.filter((checkbox) => currentSpecs.value[checkbox]);
  };
  const areAllInitialize = (option, type = "answers") => {
    return currentSpecs.value[type].every((answer) => option in answer && answer[option] != null);
  };

  const initializeOptionsCheckbox = (checkbox, type = "answers") => {
    currentCard.value.specs[type] = currentSpecs.value[type]?.map((answer) => {
      if (!(checkbox in answer) || answer[checkbox] == null) {
        answer[checkbox] = false;
      }
      return answer;
    });
  };

  const setCardPane = (pane) => (cardPane.value = pane);

  const setCurrentCardHidden = (value) => {
    if (!currentCard.value?.specs) return;
    currentCard.value.specs.hidden = value;
  };

  return {
    // state
    fields,
    settings,
    currentCard,
    currentIndex,
    currentToken,
    cardPane,
    // getters
    currentType,
    currentSubType,
    currentSpecs,
    currentConditions,
    currentIsHidden,
    preceedingCards,
    computedCardFields,
    isSettingHidden,
    isInLoop,
    // actions
    selectCard,
    updateCardName,
    updateCardType,
    initializeOptionsCheckbox,
    isCheckedOptions,
    areAllInitialize,
    setCurrentCardHidden,
    setCardPane,
  };
});
